// src/pages/performance/ManageAppraisalsQuestionsTab.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/api";

import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";

import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";

import { API, APPLICABILITY_OPTIONS } from "./manageAppraisals.constants";
import { cleanParams, normalizePageResponse, safeMsg } from "./manageAppraisals.utils";
import { DeleteQuestionDialog, QuestionEditorDialog } from "./ManageAppraisalsQuestionDialogs";

export default function QuestionsTab({ searchQuery = "" }) {
  const theme = useTheme();
  const externalSearch = useMemo(() => String(searchQuery || "").trim(), [searchQuery]);
  const lastExternalRef = useRef(externalSearch);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingRow, setDeletingRow] = useState(null);

  const [draft, setDraft] = useState({ search: externalSearch, applicability: "" });
  const [applied, setApplied] = useState({ search: externalSearch, applicability: "" });

  useEffect(() => {
    if (lastExternalRef.current === externalSearch) return;
    lastExternalRef.current = externalSearch;

    setDraft((prev) => ({ ...prev, search: externalSearch }));
    setApplied((prev) => ({ ...prev, search: externalSearch }));
    setPage(0);
  }, [externalSearch]);

  const applyFilters = useCallback(() => {
    setError("");
    setSuccess("");
    setPage(0);
    setApplied({ search: draft.search?.trim() || "", applicability: draft.applicability || "" });
  }, [draft]);

  const resetFilters = useCallback(() => {
    setDraft({ search: "", applicability: "" });
    setApplied({ search: "", applicability: "" });
    setPage(0);
  }, []);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = cleanParams({
        per_page: pageSize,
        page: page + 1,
        search: applied.search || undefined,
        applicability: applied.applicability || undefined,
      });

      const res = await api.get(API.questions, { params });
      const { rows: normalizedRows, total } = normalizePageResponse(res);

      setRows(Array.isArray(normalizedRows) ? normalizedRows : []);
      setRowCount(Number.isFinite(total) ? total : 0);
    } catch (err) {
      setError(safeMsg(err, "Failed to load questions"));
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, applied]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 90 },
      { field: "title", headerName: "Title", flex: 1, minWidth: 260 },
      {
        field: "description",
        headerName: "Description",
        flex: 1,
        minWidth: 260,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ whiteSpace: "normal", lineHeight: 1.4 }}>
            {params.row?.description && params.row.description.trim() !== "" ? params.row.description : "—"}
          </Typography>
        ),
      },
      {
        field: "applicability",
        headerName: "Applicability",
        width: 160,
        renderCell: (p) => (
          <Chip
            size="small"
            label={p.value}
            sx={{
              borderRadius: "8px",
              bgcolor:
                p.value === "YES"
                  ? alpha(theme.palette.success.main, 0.12)
                  : p.value === "NO"
                  ? alpha(theme.palette.error.main, 0.12)
                  : alpha(theme.palette.warning.main, 0.12),
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              fontWeight: 900,
            }}
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingRow(params.row);
                  setEditorOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeletingRow(params.row);
                  setDeleteOpen(true);
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [theme]
  );

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: "8px",
          border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
          bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.96 : 0.9),
          boxShadow: "none",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={1.25}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              size="small"
              label="Search"
              value={draft.search}
              onChange={(e) => setDraft((p) => ({ ...p, search: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilters();
              }}
              placeholder="Search title or description"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: "100%", sm: 280 },
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              }}
            />

            <TextField
              select
              size="small"
              label="Applicability"
              value={draft.applicability}
              onChange={(e) => setDraft((p) => ({ ...p, applicability: e.target.value }))}
              sx={{
                minWidth: { xs: "100%", sm: 180 },
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              }}
            >
              <MenuItem value="">All</MenuItem>
              {APPLICABILITY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <Button variant="outlined" onClick={resetFilters} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 800 }}>
              Reset
            </Button>

            <Button
              variant="contained"
              onClick={applyFilters}
              sx={{ borderRadius: "8px", boxShadow: "none", textTransform: "none", fontWeight: 900 }}
            >
              Apply
            </Button>

            <Tooltip title="Refresh">
              <IconButton onClick={fetchQuestions} sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Chip
              size="small"
              label={`Total: ${rowCount || 0}`}
              sx={{
                fontWeight: 900,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                alignSelf: { xs: "flex-start", sm: "center" },
              }}
            />
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingRow(null);
              setEditorOpen(true);
            }}
            sx={{ borderRadius: "8px", boxShadow: "none", textTransform: "none", fontWeight: 900 }}
          >
            Add Question
          </Button>
        </Stack>
      </Paper>

      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: "8px" }}>
          {error}
        </Alert>
      </Collapse>

      <Collapse in={!!success}>
        <Alert severity="success" sx={{ mb: 1.5, borderRadius: "8px" }}>
          {success}
        </Alert>
      </Collapse>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "8px",
          border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
          overflow: "hidden",
          boxShadow: "none",
        }}
      >
        {loading && <LinearProgress />}
        <Box sx={{ height: 620 }}>
          <DataGrid
            rows={rows || []}
            columns={columns}
            getRowId={(r) => r?.id ?? r?.question_id ?? r?.uuid}
            rowCount={rowCount}
            loading={loading}
            paginationMode="server"
            pageSizeOptions={[25, 50, 75, 100]}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            disableRowSelectionOnClick
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              },
              "& .MuiDataGrid-row:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.035),
              },
              "& .MuiDataGrid-cell": {
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
              },
            }}
          />
        </Box>
      </Paper>

      <QuestionEditorDialog
        open={editorOpen}
        initial={editingRow}
        onClose={() => setEditorOpen(false)}
        onSaved={(msg) => {
          setSuccess(msg);
          fetchQuestions();
        }}
        onError={setError}
      />

      <DeleteQuestionDialog
        open={deleteOpen}
        row={deletingRow}
        onClose={() => setDeleteOpen(false)}
        onDeleted={(msg) => {
          setSuccess(msg);
          fetchQuestions();
        }}
        onError={setError}
      />
    </Box>
  );
}
